import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import { Layers, Globe, Cpu, Users } from 'lucide-react';
import useScrollAnimation from '../hooks/useScrollAnimation';

const WhyChooseUs = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const observe = useScrollAnimation();

  const reasons = [
    {
      icon: Layers,
      title: t.reason1Title,
      desc: t.reason1Desc,
      bgClass: 'bg-badge-light',
      colorClass: 'text-primary',
    },
    {
      icon: Globe,
      title: t.reason2Title,
      desc: t.reason2Desc,
      bgClass: 'bg-accent-orange-bg',
      colorClass: 'text-accent-orange',
    },
    {
      icon: Cpu,
      title: t.reason3Title,
      desc: t.reason3Desc,
      bgClass: 'bg-accent-green-bg',
      colorClass: 'text-accent-green',
    },
    {
      icon: Users,
      title: t.reason4Title,
      desc: t.reason4Desc,
      bgClass: 'bg-accent-purple-bg',
      colorClass: 'text-accent-purple',
    },
  ];

  return (
    <section className="py-20 bg-surface-section border-b border-edge-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Badge */}
        <div className="text-center mb-4">
          <span
            ref={observe}
            className="animate-fade-up inline-flex items-center justify-center px-6 py-2 rounded-full bg-badge text-primary-dark text-sm sm:text-base font-bold font-almarai mb-4"
          >
            {t.whyChooseUsSubtitle}
          </span>
        </div>

        <h2
          ref={observe}
          className="animate-fade-up text-center font-bold font-ibm mb-4 text-heading"
          style={{ fontSize: 'clamp(24px, 5vw, 36px)', transitionDelay: '0.1s' }}
        >
          {t.whyChooseUsTitle}
        </h2>

        <p
          ref={observe}
          className="animate-fade-up text-center font-almarai mb-16 max-w-2xl mx-auto text-body-muted"
          style={{ fontSize: '16px', transitionDelay: '0.2s' }}
        >
          {t.whyChooseUsDescription}
        </p>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          {reasons.map((reason, index) => {
            const IconComponent = reason.icon;
            const isPrimary = index === 0 || index === 3;
            return (
              <div
                key={index}
                ref={observe}
                className={'animate-on-scroll group p-6 sm:p-8 rounded-2xl transition-all duration-300 hover:-translate-y-1 hover:scale-[1.01] hover:shadow-lg ' + (isPrimary ? 'bg-primary text-white shadow-lg border border-primary' : 'bg-white border-t border-x border-edge-card border-b-4 border-b-accent-orange shadow-card-soft')}
                style={{
                  transitionDelay: `${index * 0.1}s`,
                }}
              >
                
                <div
                  className={'w-14 h-14 rounded-xl flex items-center justify-center mb-5 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3 ' + (isPrimary ? 'bg-white/15' : reason.bgClass)}
                >
                  <IconComponent size={28} className={isPrimary ? 'text-white' : reason.colorClass} strokeWidth={2} />
                </div>
              
                <h3
                  className={'font-bold font-ibm mb-3 ' + (isPrimary ? 'text-white' : 'text-heading')}
                  style={{ fontSize: '20px' }}
                >
                  {reason.title}
                </h3>

                <p
                  className={'font-almarai leading-relaxed ' + (isPrimary ? 'text-white/90' : 'text-body-muted')}
                  style={{ fontSize: '16px' }}
                >
                  {reason.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
